/* Turn a component type + the user's current control values into a small a2ui
 * component tree the real renderer can render. Scalar/enum/bool/number/list
 * props become literal BoundValues; structural props (child / children /
 * tabItems) get sensible sample content so containers actually show something. */
import type { Component, CatalogComponentDef, CatalogPropertyDef } from "./engine/types";

let seq = 0;
const nid = (p = "c") => `${p}${seq++}`;

function textNode(id: string, t: string, hint?: string): Component {
  const props: Record<string, unknown> = { text: { literalString: t } };
  if (hint) props.usageHint = { literalString: hint };
  return { id, componentProperties: { Text: props } };
}

function wrapLiteral(catType: string, v: unknown): unknown {
  switch (catType) {
    case "BoundString": return { literalString: v == null ? "" : String(v) };
    case "BoundBoolean": return { literalBoolean: Boolean(v) };
    case "BoundNumber": return { literalNumber: typeof v === "number" ? v : Number(v) || 0 };
    case "BoundList": return { literalList: Array.isArray(v) ? v : [] };
    default: return v;
  }
}

function refChild(host: string, prop: string, id: string): Component {
  if (host === "Button") return textNode(id, "Click me");
  if (host === "Card") return textNode(id, "Card content");
  if (host === "Modal" && prop === "entryPointChild") return textNode(id, "Open dialog");
  if (host === "Modal" && prop === "contentChild") return textNode(id, "This is the dialog body.");
  return textNode(id, "Content");
}

function listChildren(host: string): Component[] {
  if (host === "Form") {
    const fld = nid("fld"), lbl = nid("lbl"), btn = nid("btn");
    return [
      { id: fld, componentProperties: { TextField: { label: { literalString: "Email" }, textFieldType: { literalString: "shortText" } } } },
      textNode(lbl, "Submit"),
      { id: btn, componentProperties: { Button: { child: { componentId: lbl }, primary: { literalBoolean: true }, action: { name: { literalString: "submit" } } } } },
    ];
  }
  return [textNode(nid("i"), "First item"), textNode(nid("i"), "Second item"), textNode(nid("i"), "Third item")];
}

export function buildPreviewTree(
  type: string,
  def: CatalogComponentDef | undefined,
  values: Record<string, unknown>,
): { components: Component[]; rootId: string } {
  seq = 0;
  const extra: Component[] = [];
  const fields: Record<string, unknown> = {};
  const props = Object.entries(def?.properties ?? {}) as [string, CatalogPropertyDef][];

  for (const [name, pdef] of props) {
    const v = values[name];
    if (pdef.type === "ComponentRef") {
      const cid = nid("ref");
      extra.push(refChild(type, name, cid));
      fields[name] = { componentId: cid };
    } else if (pdef.type === "ComponentList") {
      const kids = listChildren(type);
      extra.push(...kids);
      fields[name] = { explicitList: kids.map((k) => k.id) };
    } else if (pdef.type === "ActionDescriptor") {
      fields[name] = { name: { literalString: name } };
    } else if (type === "Tabs" && name === "tabItems") {
      const t1 = nid("tab"), t2 = nid("tab");
      extra.push(textNode(t1, "First tab content"), textNode(t2, "Second tab content"));
      fields[name] = { literalList: [{ label: "Overview", componentId: t1 }, { label: "Details", componentId: t2 }] };
    } else if (v !== undefined && !(v === "" && !pdef.required)) {
      fields[name] = wrapLiteral(pdef.type, v);
    }
  }

  const root: Component = { id: "root", componentProperties: { [type]: fields } };
  return { components: [root, ...extra], rootId: "root" };
}
